import React, { Component } from "react";
import styled from "styled-components";
import { changeLocation } from "api/Location";

import Auth from "../routes/Auth";
import { patchRequest } from "../api/ApiService";
import { clearAppGeneralInfoCache } from "../api/AppGeneralInfoService";
import EventEmitter from "../services/event-emitter";
import { resolveFirstName } from "../services/utils";
import introVideo from "./assets/Welcome to ITB 2.0.mp4";
import mgLogo from "./assets/New MG Logo (Icon).png";
import mgLogoDark from "./assets/New MG Logo (Dark BG) (1).png";

const API = process.env.REACT_APP_API_URL;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: #0F172A;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  flex-direction: column;
  padding: 24px;
  font-family: 'Mukta Vaani', 'Inter', sans-serif;
`;

const LogoutLink = styled.button`
  position: fixed;
  top: 20px;
  right: 24px;
  padding: 8px 14px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.72);
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.01em;
  z-index: 10;

  &:hover {
    color: #FFFFFF;
    text-decoration: underline;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 0 0 22px;
  max-width: 1000px;
`;

const TitleLogo = styled.img`
  width: 44px;
  height: 44px;
  display: block;
  flex-shrink: 0;
`;

const Title = styled.h1`
  margin: 0;
  font-family: 'Bricolage Grotesque', 'Mukta Vaani', 'Inter', sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: #FFFFFF;
  letter-spacing: -0.01em;
  text-align: left;
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 1000px;
  aspect-ratio: 16 / 9;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  display: block;
  cursor: pointer;
`;

const SoundToggle = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: rgba(15, 23, 42, 0.72);
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  backdrop-filter: blur(6px);
  z-index: 3;

  &:hover {
    background: rgba(30, 41, 59, 0.85);
  }
`;

const CenterPlayButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 84px;
  height: 84px;
  border-radius: 50%;
  border: none;
  background: rgba(37, 99, 235, 0.92);
  color: #FFFFFF;
  cursor: pointer;
  display: ${p => p.visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
  z-index: 2;
  transition: transform 120ms ease, background 120ms ease;

  &:hover {
    background: rgba(29, 78, 216, 1);
    transform: translate(-50%, -50%) scale(1.05);
  }

  svg {
    width: 34px;
    height: 34px;
    fill: currentColor;
  }
`;

const BottomBar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 16px 12px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.55) 100%);
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 2;
`;

const SmallPlayPauseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.14);
  color: #FFFFFF;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.24);
  }

  svg {
    width: 14px;
    height: 14px;
    fill: currentColor;
  }
`;

const TimeText = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`;

const ProgressTrack = styled.div`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.2);
  overflow: hidden;
  cursor: default;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #2563EB;
  width: ${p => p.percent}%;
  transition: width 200ms linear;
`;

const ReadyButton = styled.button`
  margin-top: 28px;
  padding: 14px 40px;
  border: none;
  border-radius: 12px;
  background: #2563EB;
  color: #FFFFFF;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 120ms ease, background 120ms ease;
  box-shadow: 0 10px 30px rgba(37, 99, 235, 0.35);

  &:hover:not(:disabled) {
    background: #1D4ED8;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #334155;
    color: rgba(255, 255, 255, 0.55);
    cursor: default;
    box-shadow: none;
  }
`;

const HelperText = styled.div`
  margin-top: 18px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.65);
  text-align: center;
  line-height: 1.5;
  max-width: 520px;
`;

const GreetingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 1100px;
  padding: 0 24px;
`;

const GreetingHi = styled.div`
  font-size: 22px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.78);
  margin-bottom: 18px;
  letter-spacing: 0.01em;
`;

const GreetingTitle = styled.h1`
  margin: 0;
  font-family: 'Bricolage Grotesque', 'Mukta Vaani', 'Inter', sans-serif;
  font-size: 50px;
  line-height: 1.08;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #FFFFFF;
`;

const GreetingTitleAccent = styled.span`
  display: block;
  color: #5C91F5;
`;

const GreetingLogo = styled.img`
  width: 115px;
  height: 115px;
  margin: 44px 0 36px;
  display: block;
`;

const ContinueButton = styled.button`
  padding: 14px 50px;
  border: none;
  border-radius: 9px;
  background: #2563EB;
  color: #FFFFFF;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 120ms ease, background 120ms ease;
  box-shadow: 0 11px 32px rgba(37, 99, 235, 0.45);

  &:hover {
    background: #1D4ED8;
    transform: translateY(-1px);
  }
`;

const PlayIconSvg = () => (
  <svg viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIconSvg = () => (
  <svg viewBox="0 0 24 24">
    <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
  </svg>
);

const formatTime = (seconds) => {
  if (!seconds || !isFinite(seconds)) return "0:00";
  const total = Math.floor(seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

class IntroVideoPage extends Component {
  state = {
    showGreeting: true,
    ended: false,
    muted: false,
    submitting: false,
    paused: true,
    currentTime: 0,
    duration: 0,
  };

  handleContinueFromGreeting = () => {
    this.setState({ showGreeting: false });
  };

  // Furthest playback position the user has reached. Any attempt to
  // seek beyond this snaps back — a defensive guard in case some input
  // path (keyboard, media keys, browser extension) tries to skip ahead.
  maxTime = 0;

  componentDidMount() {
    const user = Auth.getUser();
    if (user && user.intro !== 0) {
      changeLocation("/start");
    }
  }

  handleTimeUpdate = () => {
    if (!this.videoRef) return;
    const current = this.videoRef.currentTime;
    if (current > this.maxTime) this.maxTime = current;
    this.setState({ currentTime: current });
  };

  handleLoadedMetadata = () => {
    if (!this.videoRef) return;
    this.setState({ duration: this.videoRef.duration || 0 });
  };

  // Block any seek that would move past the furthest point already
  // watched. Rewinding is also blocked (we snap back to maxTime) so
  // the student can't scrub back-and-forth to game the "watched"
  // check either.
  handleSeeking = () => {
    if (!this.videoRef) return;
    const tolerance = 0.5;
    if (Math.abs(this.videoRef.currentTime - this.maxTime) > tolerance) {
      this.videoRef.currentTime = this.maxTime;
    }
  };

  handleVolumeChange = () => {
    if (!this.videoRef) return;
    if (this.videoRef.muted !== this.state.muted) {
      this.setState({ muted: this.videoRef.muted });
    }
  };

  handlePlay = () => {
    this.setState({ paused: false });
  };

  handlePause = () => {
    this.setState({ paused: true });
  };

  handleEnded = () => {
    this.setState({ ended: true, paused: true });
  };

  togglePlayPause = () => {
    if (!this.videoRef) return;
    if (this.videoRef.paused || this.videoRef.ended) {
      this.videoRef.play().catch(() => {});
    } else {
      this.videoRef.pause();
    }
  };

  handleToggleSound = () => {
    this.setState(
      prev => ({ muted: !prev.muted }),
      () => {
        if (this.videoRef && this.state.muted === false) {
          this.videoRef.play().catch(() => {});
        }
      }
    );
  };

  handleLogout = () => {
    if (Auth.isUserAuthenticated()) {
      Auth.deauthenticateUser();
    }
    changeLocation("/login");
  };

  handleReady = () => {
    if (this.state.submitting) return;
    const userId = Auth.getUserId();
    if (!userId) {
      changeLocation("/start");
      return;
    }

    this.setState({ submitting: true });
    patchRequest(`${API}users/${userId}/`)
      .send({ intro: 1 })
      .then(() => {
        const user = Auth.getUser();
        if (user) Auth.setUserData({ ...user, intro: 1 });
        // Bust the general-info cache AND ping App.js to refetch.
        // Clearing the cache alone isn't enough — App.js's fetchData
        // only runs on componentDidMount or when the "reload-data"
        // event fires, and route changes via changeLocation don't
        // remount App or invoke componentDidUpdate's fetch path.
        // Without the emit, App.js reads the pre-video cached response
        // (intro=0) on the next render, early-returns before writing
        // players_data, and downstream views like the Team Profile
        // popup show empty until the user hard-refreshes.
        clearAppGeneralInfoCache();
        EventEmitter.getInstance().trigger("reload-data");
        changeLocation("/start");
      })
      .catch(() => {
        // Same reasoning as the success path — even if the PATCH
        // failed we're navigating away, so drop the cache and fire
        // a refetch so App.js's next tick isn't served the stale
        // intro=0 response.
        clearAppGeneralInfoCache();
        EventEmitter.getInstance().trigger("reload-data");
        changeLocation("/start");
      });
  };

  render() {
    const { showGreeting, ended, muted, submitting, paused, currentTime, duration } = this.state;

    if (showGreeting) {
      const user = Auth.getUser();
      const firstName = resolveFirstName(user && user.first_name);
      return (
        <Overlay>
          <LogoutLink onClick={this.handleLogout}>Logout</LogoutLink>
          <GreetingWrap>
            {firstName && <GreetingHi>Hi {firstName},</GreetingHi>}
            <GreetingTitle>
              Welcome to Market Games'
              <GreetingTitleAccent>Intro to Business Challenge</GreetingTitleAccent>
            </GreetingTitle>
            <GreetingLogo src={mgLogoDark} alt="Market Games" />
            <ContinueButton onClick={this.handleContinueFromGreeting}>
              Continue
            </ContinueButton>
          </GreetingWrap>
        </Overlay>
      );
    }

    const percent = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
    // Unlock the "I'm Ready" button at the 85-second mark so students
    // aren't forced to sit through the tail of the intro before
    // continuing. Video-end still counts (safety net for shorter cuts).
    const canProceed = ended || currentTime >= 85;

    return (
      <Overlay>
        <LogoutLink onClick={this.handleLogout}>Logout</LogoutLink>
        <TitleRow>
          <TitleLogo src={mgLogo} alt="Market Games" />
          <Title>The Intro to Business Challenge</Title>
        </TitleRow>
        <VideoWrapper>
          <Video
            innerRef={el => { this.videoRef = el; }}
            src={introVideo}
            muted={muted}
            playsInline
            disablePictureInPicture
            tabIndex="-1"
            onClick={this.togglePlayPause}
            onPlay={this.handlePlay}
            onPause={this.handlePause}
            onEnded={this.handleEnded}
            onTimeUpdate={this.handleTimeUpdate}
            onLoadedMetadata={this.handleLoadedMetadata}
            onSeeking={this.handleSeeking}
            onVolumeChange={this.handleVolumeChange}
            onContextMenu={e => e.preventDefault()}
          />
          <CenterPlayButton
            type="button"
            visible={paused && !ended}
            onClick={this.togglePlayPause}
            aria-label="Play"
          >
            <PlayIconSvg />
          </CenterPlayButton>
          <SoundToggle type="button" onClick={this.handleToggleSound}>
            {muted ? "🔇  Unmute" : "🔊  Mute"}
          </SoundToggle>
          <BottomBar>
            <SmallPlayPauseButton
              type="button"
              onClick={this.togglePlayPause}
              aria-label={paused ? "Play" : "Pause"}
            >
              {paused ? <PlayIconSvg /> : <PauseIconSvg />}
            </SmallPlayPauseButton>
            <TimeText>{formatTime(currentTime)} / {formatTime(duration)}</TimeText>
            <ProgressTrack>
              <ProgressFill percent={percent} />
            </ProgressTrack>
          </BottomBar>
        </VideoWrapper>
        <ReadyButton onClick={this.handleReady} disabled={!canProceed || submitting}>
          {submitting ? "Saving..." : "I'm Ready"}
        </ReadyButton>
        <HelperText>
          {canProceed
            ? "Great! Click the button above to continue."
            : "Press play to watch the intro. The button below will unlock as you watch."}
        </HelperText>
      </Overlay>
    );
  }
}

export default IntroVideoPage;



// WEBPACK FOOTER //
// ./src/containers/intro-video-page.js